import { useContext, useMemo } from 'react'

import { Maybe, SanityRoleBasedContent } from '../../../graphql/gatsby'
import { SanityUserRolesContext } from '../context'
import { isSanityUserRole, SanityUserRole } from '../roles'

export type UseSanityRoleBasedContentOptions = SanityShouldDisplayRoleBasedContentOptions

export type UseSanityRoleBasedContentLazy = UseSanityRoleBasedContent & {
  shouldDisplayContent: (options: SanityShouldDisplayRoleBasedContentOptions) => boolean
}

export type UseSanityRoleBasedContentEager = UseSanityRoleBasedContent & {
  shouldDisplay: boolean
}

interface UseSanityRoleBasedContent {
  isLoading: boolean
  userRoles: readonly SanityUserRole[]
}

export type SanityShouldDisplayRoleBasedContent = (
  options: SanityShouldDisplayRoleBasedContentOptions,
) => boolean

export interface SanityShouldDisplayRoleBasedContentOptions {
  roleBasedContent?: Maybe<SanityRoleBasedContent>
}

const shouldDisplayContentFor =
  (userRoles: readonly SanityUserRole[]) =>
  ({ roleBasedContent }: SanityShouldDisplayRoleBasedContentOptions): boolean => {
    if (!roleBasedContent || roleBasedContent.enabled !== true) {
      return true
    }

    const roles = (roleBasedContent.roles || []).filter(isSanityUserRole)

    return roles.some((role) => userRoles.includes(role))
  }

export function useSanityRoleBasedContent(): UseSanityRoleBasedContentLazy
export function useSanityRoleBasedContent(
  options: UseSanityRoleBasedContentOptions,
): UseSanityRoleBasedContentEager
export function useSanityRoleBasedContent(
  options?: UseSanityRoleBasedContentOptions,
): UseSanityRoleBasedContentEager | UseSanityRoleBasedContentLazy {
  const { isLoading, userRoles } = useContext(SanityUserRolesContext)

  return useMemo(
    () =>
      options
        ? {
            isLoading,
            userRoles,
            shouldDisplay: shouldDisplayContentFor(userRoles)(options),
          }
        : {
            isLoading,
            userRoles,
            shouldDisplayContent: shouldDisplayContentFor(userRoles),
          },
    [isLoading, userRoles, options],
  )
}
